import type { NavbarButtonProps } from '@carboncopyco/ui';
import { Navbar } from '@carboncopyco/ui';
import { forwardRef, useEffect, useState } from 'react';
import {
  useEditorGuidanceTooltips,
  useEditorLoaded,
} from '../../context/EditorSdkProvider';

export interface EditorNavbarButtonProps
  extends Pick<
    NavbarButtonProps,
    'onClick' | 'label' | 'variant' | 'disabled' | 'icon' | 'tooltipMessage'
  > {
  disabledWhileLoading?: boolean;
}

export const EditorNavbarButton = forwardRef<
  HTMLButtonElement,
  EditorNavbarButtonProps
>(
  (
    {
      onClick,
      label,
      disabled,
      disabledWhileLoading = true,
      variant,
      icon,
      tooltipMessage,
    },
    ref
  ) => {
    const isEditorLoaded = useEditorLoaded();
    const {
      stopGuidanceTooltipsMode,
      isGuidanceTooltipsEditLoading,
      isGuidanceTooltipOpen,
      setIsGuidanceTooltipsEditLoading,
    } = useEditorGuidanceTooltips();

    const [isWaitingForTooltipRequest, setIsWaitingForTooltipRequest] =
      useState(false);

    useEffect(() => {
      if (!isGuidanceTooltipsEditLoading && isWaitingForTooltipRequest) {
        setIsWaitingForTooltipRequest(false);
        onClick(null);
      }
    }, [isGuidanceTooltipsEditLoading, isWaitingForTooltipRequest]);

    return (
      <Navbar.Button
        variant={variant}
        label={label}
        ref={ref}
        onClick={(e) => {
          // Specific case where we have tooltip opened. We need first to close it and wait for request to be completed.
          // Then we can go to preview, which is handled in useEffect
          if (isGuidanceTooltipOpen) {
            setIsGuidanceTooltipsEditLoading(true);
            setIsWaitingForTooltipRequest(true);
            stopGuidanceTooltipsMode();
            return;
          }
          onClick(e);
        }}
        disabled={
          (disabledWhileLoading && !isEditorLoaded) ||
          disabled ||
          isGuidanceTooltipsEditLoading
        }
        icon={icon}
        tooltipMessage={tooltipMessage}
      />
    );
  }
);
EditorNavbarButton.displayName = 'EditorNavbarButton';
